import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { docsApi, exportsApi, mattersApi, researchApi, vaultApi } from "@/lib/api";
import { useAsyncJob } from "@/lib/useAsyncJob";
import { JobProgress } from "@/components/StrategyPanel";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ArrowSquareOut, BookmarkSimple, DownloadSimple, LinkSimple, MagnifyingGlass, SpeakerHigh, Sparkle, Warning } from "@phosphor-icons/react";
import VoiceButton from "@/components/VoiceButton";
import { SourceChip } from "@/components/SourceChip";
import { toast } from "sonner";
import HelpButton from "@/components/HelpButton";

const EXAMPLES = [
    "Research the current law on condonation of delay in the Supreme Court and cite all authorities.",
    "Delhi High Court case law on interim injunction in trademark matters, with citations.",
    "Compare Supreme Court vs Delhi High Court position on anticipatory bail in economic offences.",
    "Latest judgments on limitation under Section 5 of the Limitation Act, 1963.",
];

export default function Research() {
    const [sp] = useSearchParams();
    const navigate = useNavigate();
    const [provider, setProvider] = useState(null);
    const [query, setQuery] = useState("");
    const [language, setLanguage] = useState("en");
    const [matterId, setMatterId] = useState(sp.get("matter") || "none");
    const [matters, setMatters] = useState([]);
    const [memos, setMemos] = useState([]);
    const [active, setActive] = useState(null);
    const [saveAsItem, setSaveAsItem] = useState(false);
    const { job, busy: loading, error: jobError, start: startJob, reset: resetJob } = useAsyncJob({ intervalMs: 2000, maxPolls: 90 });

    const loadAll = async () => {
        const [p, ms, mms] = await Promise.all([researchApi.status(), researchApi.memos(), mattersApi.list()]);
        setProvider(p);
        setMemos(ms);
        setMatters(mms);
    };

    useEffect(() => {
        loadAll();
    }, []);

    useEffect(() => {
        const memo = sp.get("memo");
        if (memo) {
            researchApi.memo(memo).then(setActive).catch(() => {});
        }
    }, [sp]);

    const ask = async () => {
        if (!query.trim()) return;
        setActive(null);
        const j = await startJob(() => researchApi.ask({
            query,
            matter_id: matterId === "none" ? null : matterId,
            language,
        }));
        if (j?.status === "completed" && j.result) {
            setActive(j.result);
            await loadAll();
        } else if (j?.status === "failed") {
            toast.error(jobError || j.error || "Research failed");
        }
    };

    const saveToVault = async () => {
        if (!active) return;
        try {
            await vaultApi.create({
                title: active.query.slice(0, 100),
                body: active.answer + "\n\n" + (active.citations || []).map((c, i) => `[${i + 1}] ${c.title || ""} ${c.citation || ""}`).join("\n"),
                kind: "precedent",
                tags: ["research"],
                language: active.language || language,
            });
            toast.success("Saved to Knowledge Vault");
        } catch {
            toast.error("Could not save to vault");
        }
    };

    const speakAnswer = async () => {
        if (!active?.answer) return;
        try {
            const blob = await (await import("@/lib/api")).voiceApi.speak(active.answer.slice(0, 3800));
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            audio.play();
        } catch (e) {
            console.error(e);
            toast.error("Voice output failed");
        }
    };

    return (
        <div className="h-[calc(100vh-57px)] grid grid-cols-12" data-testid="research-page">
            {/* left: memo history */}
            <aside className="col-span-12 lg:col-span-3 border-r border-border bg-white/60 overflow-y-auto scrollbar-slim p-4">
                <div className="rule-line pt-2 mb-4">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Research memos</div>
                    <div className="font-serif text-2xl tracking-tight">History</div>
                </div>
                {!memos.length ? (
                    <div className="text-xs text-muted-foreground">No research yet. Ask a question.</div>
                ) : (
                    <div className="space-y-1">
                        {memos.map((m) => (
                            <button
                                key={m.id}
                                onClick={() => setActive(m)}
                                className={`w-full text-left p-2 rounded-sm border ${active?.id === m.id ? "border-primary bg-primary/5" : "border-transparent hover:bg-muted/60"}`}
                                data-testid={`memo-item-${m.id}`}
                            >
                                <div className="text-xs font-medium line-clamp-2">{m.query}</div>
                                <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                                    <span className={`chip ${m.provider === "perplexity_sonar" ? "chip-external" : "chip-ai"}`}>
                                        {m.provider === "perplexity_sonar" ? "web" : "ai"}
                                    </span>
                                    <span>{m.citations?.length || 0} cites</span>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </aside>

            {/* center: ask area + answer */}
            <section className="col-span-12 lg:col-span-6 overflow-y-auto scrollbar-slim p-4 md:p-8" data-testid="research-center">
                <div className="max-w-3xl mx-auto">
                    <div className="rule-line pt-3 mb-4">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Research Assistant</div>
                        <div className="flex items-center gap-2">
                            <h1 className="font-serif text-4xl tracking-tight">Ask the bench.</h1>
                            <HelpButton topic="research" testid="help-research" />
                        </div>
                    </div>

                    {provider && (
                        <div className="mb-4 flex items-start gap-2 text-sm">
                            <SourceChip type={provider.verified ? "external" : "ai"}>
                                <Sparkle size={11} weight="fill" /> {provider.label}
                            </SourceChip>
                            {!provider.verified && (
                                <p className="text-xs text-muted-foreground">
                                    AI-sourced citations. Verify each authority independently before relying on it.
                                </p>
                            )}
                        </div>
                    )}

                    <div className="border border-border rounded-sm bg-white p-3 mb-3">
                        <Textarea
                            data-testid="research-query-input"
                            placeholder="e.g., Research the current law on condonation of delay in the Supreme Court and cite all authorities."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            rows={3}
                            className="border-0 shadow-none focus-visible:ring-0 text-base resize-none"
                        />
                        <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
                            <div className="flex items-center gap-2">
                                <Select value={language} onValueChange={setLanguage}>
                                    <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="research-lang"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="en">Answer in English</SelectItem>
                                        <SelectItem value="hi">उत्तर हिन्दी में</SelectItem>
                                        <SelectItem value="mixed">Mixed HI + EN</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={matterId} onValueChange={setMatterId}>
                                    <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="research-matter"><SelectValue placeholder="Link to matter (optional)" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">No matter</SelectItem>
                                        {matters.map((m) => (
                                            <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-center gap-2">
                                <VoiceButton compact onTranscript={({ text }) => setQuery(text)} testid="research-mic" />
                                <Button onClick={ask} disabled={loading} data-testid="research-ask-btn">
                                    {loading ? "Researching…" : (<><MagnifyingGlass size={14} /> Research</>)}
                                </Button>
                            </div>
                        </div>
                    </div>

                    {loading && job && (
                        <div className="border border-border bg-white rounded-md p-3 mt-4" data-testid="research-progress">
                            <div className="text-xs text-muted-foreground">Running research — this can take 30-90 seconds for detailed queries.</div>
                            <JobProgress job={job} />
                        </div>
                    )}
                    {jobError && !loading && (
                        <div className="border border-destructive/40 bg-destructive/5 rounded-md p-3 mt-4 text-xs" data-testid="research-error">
                            <b>Research failed:</b> {jobError}{" "}
                            <button onClick={resetJob} className="underline ml-1">dismiss</button>
                        </div>
                    )}

                    {!active && !loading && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-6">
                            {EXAMPLES.map((ex) => (
                                <button
                                    key={ex}
                                    onClick={() => setQuery(ex)}
                                    className="text-left text-sm border border-border rounded-sm p-3 bg-white hover:border-primary/40 hover:bg-muted/40 transition-all"
                                >
                                    <Sparkle size={12} weight="fill" className="text-primary inline-block mr-2" /> {ex}
                                </button>
                            ))}
                        </div>
                    )}

                    {active && (
                        <div className="mt-6 fade-in" data-testid="research-answer">
                            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                                <div className="flex items-center gap-2">
                                    <SourceChip type={active.provider === "perplexity_sonar" ? "external" : "ai"}>
                                        {active.provider === "perplexity_sonar" ? "Verified web" : "AI-sourced"}
                                    </SourceChip>
                                    {active.confidence && (
                                        <span className="chip chip-doc">Confidence: {active.confidence}</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" onClick={speakAnswer} data-testid="research-speak-btn">
                                        <SpeakerHigh size={14} /> Read aloud
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={saveToVault} data-testid="research-save-vault-btn">
                                        <BookmarkSimple size={14} /> Save to Vault
                                    </Button>
                                    <a
                                        href={exportsApi.memoDocx(active.id)}
                                        className="inline-flex items-center gap-1 text-xs border border-border rounded-sm px-3 h-8"
                                        data-testid="research-export-docx"
                                    >
                                        <DownloadSimple size={14} /> DOCX
                                    </a>
                                    <a
                                        href={exportsApi.memoPdf(active.id)}
                                        className="inline-flex items-center gap-1 text-xs border border-border rounded-sm px-3 h-8"
                                        data-testid="research-export-pdf"
                                    >
                                        <DownloadSimple size={14} /> PDF
                                    </a>
                                </div>
                            </div>

                            <div className="font-serif text-2xl leading-snug mb-3">{active.query}</div>

                            <div className="legal-sheet">
                                <div className="whitespace-pre-wrap text-[15px] leading-relaxed">
                                    {renderWithInlineMarkers(active.answer)}
                                </div>
                            </div>

                            {active.date_sensitivity && (
                                <div className="mt-3 border border-amber-300 bg-amber-50 text-amber-900 rounded-sm p-3 text-sm flex gap-2">
                                    <Warning size={16} className="shrink-0 mt-0.5" weight="fill" />
                                    <div><b>Time-sensitive:</b> {active.date_sensitivity}</div>
                                </div>
                            )}
                            {active.contradictions && (
                                <div className="mt-3 border border-secondary/40 bg-secondary/5 rounded-sm p-3 text-sm">
                                    <b>Contradictions flagged:</b> {active.contradictions}
                                </div>
                            )}
                            {active.disclaimer && (
                                <div className="mt-3 text-xs text-muted-foreground italic">{active.disclaimer}</div>
                            )}
                        </div>
                    )}
                </div>
            </section>

            {/* right: citations panel */}
            <aside className="col-span-12 lg:col-span-3 border-l border-border bg-white/60 overflow-y-auto scrollbar-slim p-4" data-testid="research-right">
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">Authorities & Sources</div>
                {!active?.citations?.length ? (
                    <div className="text-xs text-muted-foreground">Citations will appear here.</div>
                ) : (
                    <ol className="space-y-3">
                        {active.citations.map((c, i) => (
                            <li key={c.id || i} className="border border-border rounded-sm p-3 bg-white">
                                <div className="flex items-start justify-between gap-2">
                                    <span className="citation-marker">{c.id || i + 1}</span>
                                    <span className="chip chip-doc capitalize">{c.type || "case_law"}</span>
                                </div>
                                <div className="font-serif text-base leading-tight mt-1 font-medium">{c.title || "Untitled"}</div>
                                {c.court && <div className="text-xs text-muted-foreground">{c.court}</div>}
                                {c.citation && <div className="font-mono text-[11px] mt-1">{c.citation}</div>}
                                {c.date && <div className="text-[11px] text-muted-foreground">{c.date}</div>}
                                {c.relevance && <div className="text-xs mt-2 leading-snug">{c.relevance}</div>}
                                {c.url && (
                                    <a
                                        href={c.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-primary underline inline-flex items-center gap-1 mt-2"
                                    >
                                        View source <ArrowSquareOut size={11} />
                                    </a>
                                )}
                            </li>
                        ))}
                    </ol>
                )}

                {active?.jurisdiction_notes && (
                    <div className="mt-5 border border-border rounded-sm p-3 bg-white">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-1">Jurisdiction</div>
                        <div className="text-sm leading-snug">{active.jurisdiction_notes}</div>
                    </div>
                )}
            </aside>
        </div>
    );
}

function renderWithInlineMarkers(text) {
    if (!text) return null;
    const parts = text.split(/(\[\d+\])/g);
    return parts.map((p, i) => {
        const m = p.match(/^\[(\d+)\]$/);
        if (m) {
            return (
                <span key={i} className="citation-marker" title={`Citation ${m[1]}`}>
                    {m[1]}
                </span>
            );
        }
        return <span key={i}>{p}</span>;
    });
}
